import { API_BASE } from './api-base';
import { clearSentryIdentity, identifyFromInstance } from './sentry-identity';
import { decodeInstancePayload } from './wix-instance-payload';
/**
 * Client-side acquisition of the signed `-666` editor instance (mobile-parity;
 * docs/AUTH-MIDDLEWARE-AUDIT.md §4).
 *
 * Persisted in `localStorage` so it survives page reloads, with an in-memory
 * cache in front to avoid re-parsing. Re-acquired from the server route when
 * missing or near expiry. Gated by the `symphonyInstanceAuthWeb` toggle (see
 * lib/instance-auth-flag.ts) — nothing here runs unless the flag is on.
 */

// The active-mount API base (lib/api-base.ts): '' on the symphony.wix.com root,
// '/orion-web' / '/symphony-web' on the manage.wix.com mounts.
const STORAGE_KEY = 'symphonySignedInstance';
const LOG = '[instance-auth]';

// Re-acquire this long before expiry so a call never rides an about-to-expire token.
const REFRESH_SKEW_MS = 60_000;

/**
 * Discriminated acquisition outcome so the caller can route correctly:
 * - `ok`             → use the instance.
 * - `unauthenticated`→ no valid wix session; the caller redirects to login.
 * - `none`           → authenticated but no instance yet (no metasite).
 * - `error`          → acquire failed (HTTP/network); the caller must FAIL, not
 *                      fall back to the proxy — a broken instance path shouldn't
 *                      be masked by silently reverting to the middleware.
 */
export type AcquireResult =
  | { status: 'ok'; instance: string }
  | { status: 'unauthenticated' }
  | { status: 'none' }
  | { status: 'error' };

/**
 * A Wix instance is `${signature}.${base64UrlPayload}`; the payload JSON carries
 * `expirationDate` (ISO). Returns epoch ms, or null when unparseable/absent.
 */
export function parseInstanceExpiry(instance: string): number | null {
  const expirationDate = decodeInstancePayload(instance)?.expirationDate;
  if (!expirationDate) return null;
  const ms = new Date(expirationDate).getTime();
  return Number.isFinite(ms) ? ms : null;
}

// localStorage access, safe under SSR / private-mode (both can throw or be absent).
function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

// In-memory cache in front of localStorage.
let memo: { instance: string; expiresAt: number | null } | null = null;

function fresh(): string | null {
  if (!memo) {
    const stored = storage()?.getItem(STORAGE_KEY) ?? null;
    if (stored) memo = { instance: stored, expiresAt: parseInstanceExpiry(stored) };
  }
  if (!memo) return null;
  // Unparseable expiry → trust it; the api-client re-acquires on a 401.
  if (memo.expiresAt !== null && Date.now() >= memo.expiresAt - REFRESH_SKEW_MS) return null;
  // On the HANDED-OUT path, not just the freshly-fetched one: a reload rehydrates from
  // localStorage and `ensureInstance()` then returns without ever reaching `store()`, so a page
  // with no duplexer subscription would otherwise report `0 users` for its whole life. Cheap to
  // repeat — `identifyFromInstance` compares the token before decoding it. Safe to do from a
  // CACHED token because `clearInstance()` wipes this cache in the same breath as the id, so the
  // only token this can ever hand over is one the app is itself using.
  identifyFromInstance(memo.instance);
  return memo.instance;
}

function store(instance: string): void {
  memo = { instance, expiresAt: parseInstanceExpiry(instance) };
  // The freshly-acquired half of the same thing: `acquireInstance` returns its instance directly,
  // without going back through `fresh()`. See lib/sentry-identity.ts for why the id is worth this.
  identifyFromInstance(instance);
  try {
    storage()?.setItem(STORAGE_KEY, instance);
  } catch {
    // private-mode / quota — the in-memory cache still serves this session.
  }
}

let inflight: Promise<AcquireResult> | null = null;

/** Fetch a fresh instance from the server route. Concurrent callers share one request. */
export function acquireInstance(): Promise<AcquireResult> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/instance`, { credentials: 'include' });
      if (res.status === 401) {
        clearInstance();
        console.info(`${LOG} unauthenticated (401) — login required`);
        return { status: 'unauthenticated' };
      }
      if (!res.ok) {
        console.warn(`${LOG} acquire failed: HTTP ${res.status}`);
        return { status: 'error' };
      }
      const data = (await res.json()) as { instance?: string | null };
      const instance = data.instance ?? null;
      if (!instance) {
        console.info(`${LOG} no instance yet (caller has no metasite)`);
        return { status: 'none' };
      }
      store(instance);
      console.info(`${LOG} acquired instance (expires ${memo?.expiresAt ? new Date(memo.expiresAt).toISOString() : 'unknown'})`);
      return { status: 'ok', instance };
    } catch (err) {
      console.warn(`${LOG} acquire error`, err);
      return { status: 'error' };
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** The stored instance if still fresh, else acquire a new one. */
export function ensureInstance(): Promise<AcquireResult> {
  const cached = fresh();
  if (cached) return Promise.resolve({ status: 'ok', instance: cached });
  return acquireInstance();
}

/** The stored instance if fresh, else null (no fetch). */
export function getStoredInstance(): string | null {
  return fresh();
}

/** Drop the stored instance (e.g. on logout or a mid-session 401). */
export function clearInstance(): void {
  memo = null;
  clearSentryIdentity();
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
